/** @format */
import { Component, computed, HostListener, inject, input, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { selectAddToCartButtonTextSDDynamicEnabled } from './+state/add-to-cart-button.selector';

import { UtilityService, WINDOW } from '@shared/data-access/services';
import { DOCUMENT } from '@angular/common';
import {
	AddToCartButtonService,
	addToCartTriggered,
	ATC_BUTTON_TEXTS,
	atcAttemptedWithAttributeErrors,
	quantityRangeValidationFailed,
	quantityRangeValidationSucceeded,
	selectAtcUserInputQty,
	userSetInputQuantity
} from '@pdp/add-to-cart/data-access';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MinMaxQty } from '@pdp/data-access/interfaces';
import {
	selectAllProductSkus,
	selectIsComingSoon,
	selectIsPersonalizedProduct,
	selectKillswitch,
	selectResolvedSku,
	selectResolvedSkuPartNumber,
	selectSelectedPersonalizationOptions
} from '@pdp/data-access/state';
import { AttributesUtilityService } from '@pdp/attributes/data-access';
import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { MyCustomizations } from '@shared/data-access/interfaces';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HmfButtonComponent } from '@dsg-tech/homefield-angular';
import { SeeAvailableStoresButtonComponent } from '../see-available-stores-button/see-available-stores-button.component';

@Component({
	selector: 'pdp-feature-add-to-cart-button',
	templateUrl: './add-to-cart-button.component.html',
	styleUrl: './add-to-cart-button.component.scss',
	imports: [
		ReactiveFormsModule,
		FormsModule,
		HmfButtonComponent,
		SeeAvailableStoresButtonComponent
	]
})
export class AddToCartButtonComponent implements OnInit, OnDestroy {
	private readonly store = inject(Store);
	private readonly attributesUtilityService = inject(AttributesUtilityService);
	private readonly addToCartButtonService = inject(AddToCartButtonService);
	private readonly utilityService = inject(UtilityService);
	private readonly window = inject(WINDOW) as Window;
	private readonly document = inject(DOCUMENT);

	/**
	 * @description the value entered by user in the quantity input
	 */
	userInputQuantity = 1;

	/** Do we still need the below properties? **/

	showATCValidationMessage = false;

	isSecureLaunch = false;

	isInStoreOnly = false;
	addedToCart = false;

	showNotifyMeGoldMessage = false;
	showNotifyMeGoldMessageEnabled = false;

	allProductSkus: string[];
	resolvedSkuPartNumber: string;

	isPersonalizedProduct = false;

	isErrorOnAttributes = false;

	qvFavoritesAttributes = false;

	// Favorites Killswitch
	isAuthenticated = false;
	nearbyStoresButtonEnabled = this.store.selectSignal(
		selectKillswitch('nearbyStoresButtonEnabled')
	);

	private UNSUBSCRIBE$: Subject<void> = new Subject();

	storeSelectionReady = false;

	atcButtonTexts = ATC_BUTTON_TEXTS;

	readonly ecode = input('');

	readonly badge = input('');

	readonly isCustomizable = input(false);

	readonly customizedAttributes = input<MyCustomizations>(null);

	readonly showQuantityInput = input(true);

	readonly disabled = input(false);

	minQty = 1;
	maxQty = 99;

	readonly notifyUserWhenAvailableEnabled = this.store.selectSignal(
		selectKillswitch('notifyUserWhenAvailableEnabled')
	);

	readonly isComingSoon = this.store.selectSignal(selectIsComingSoon);
	readonly buttonTextFromState = this.store.selectSignal(
		selectAddToCartButtonTextSDDynamicEnabled
	);

	readonly buttonText = computed(() => {
		const isComingSoon = this.isComingSoon();
		const buttonText = this.buttonTextFromState();
		const notifyMeEnabled = this.notifyUserWhenAvailableEnabled();
		if (!buttonText) {
			return buttonText;
		}
		if (isComingSoon) {
			return {
				...buttonText,
				addToCartButtonText: 'Coming Soon',
				addToCartButtonDisabled: true
			};
		}
		// Handle notify me killswitch
		if (!notifyMeEnabled && buttonText.addToCartButtonText === ATC_BUTTON_TEXTS.notifyMe) {
			return {
				...buttonText,
				addToCartButtonText: ATC_BUTTON_TEXTS.atc,
				addToCartButtonDisabled: true
			};
		}
		return buttonText;
	});

	ngOnInit(): void {
		/**
		 * @description select the userInputQty from the store
		 */
		this.store
			.select(selectAtcUserInputQty)
			.pipe(takeUntil(this.UNSUBSCRIBE$))
			.subscribe((value) => {
				if (value !== this.userInputQuantity) {
					this.validateQuantity(value, true);
				}
			});

		this.store
			.select(selectAllProductSkus)
			.pipe(takeUntil(this.UNSUBSCRIBE$))
			.subscribe((value) => {
				this.allProductSkus = value.map((sku) => sku.partNumber);
			});

		this.store
			.select(selectResolvedSkuPartNumber)
			.pipe(takeUntil(this.UNSUBSCRIBE$))
			.subscribe((value) => {
				this.resolvedSkuPartNumber = value;
				if (value) {
					this.isErrorOnAttributes = false;
				}
			});

		this.store
			.select(selectSelectedPersonalizationOptions)
			.pipe(takeUntil(this.UNSUBSCRIBE$))
			.subscribe((value) => {
				if (value) {
					this.isErrorOnAttributes = false;
				}
			});

		this.store
			.select(selectResolvedSku)
			.pipe(takeUntil(this.UNSUBSCRIBE$))
			.subscribe((value) => {
				this.minQty = value?.prices?.minQty ? value?.prices?.minQty : this.minQty;
				this.maxQty = value?.prices?.maxQty ? value?.prices?.maxQty : this.maxQty;
				if (this.minQty > 1 && this.userInputQuantity < this.minQty) {
					this.store.dispatch(
						quantityRangeValidationFailed({
							message: `${DCSG_CONSTANTS.minQtyAlert.message} ${this.minQty}`
						})
					);
					this.userInputQuantity = this.minQty;
					this.setUserInputQuantityInState(this.userInputQuantity);
				}
			});

		this.store
			.select(selectIsPersonalizedProduct)
			.pipe(takeUntil(this.UNSUBSCRIBE$))
			.subscribe((value) => {
				this.isPersonalizedProduct = value;
			});

		if (window.headerFunctions?.storeSelection) {
			this.storeSelectionReady = true;
		}
	}

	ngOnDestroy(): void {
		this.UNSUBSCRIBE$.next();
		this.UNSUBSCRIBE$.complete();
	}

	addToCartButtonClicked(buttonType: string, buttonAction: string) {
		// if button action is SelectOptions scroll user to attributes
		if (buttonAction === 'SelectOptions') {
			this.isErrorOnAttributes = this.attributesUtilityService.validateIfAttributeErrorsExist(
				'[add-to-cart-button] addToCartButtonClicked',
				this.isPersonalizedProduct
			);
			// scroll to error
			if (this.isErrorOnAttributes) {
				this.utilityService.scrollToElement(
					this.document.getElementById('attributes-v2-container'),
					{
						behavior: 'smooth',
						block: 'center',
						inline: 'nearest'
					}
				);
				this.store.dispatch(atcAttemptedWithAttributeErrors());
			}
		} else if (buttonType === ATC_BUTTON_TEXTS.noStoreSelected) {
			const storeSelectionValues = this.resolvedSkuPartNumber
				? { skusArr: this.allProductSkus, selectedSkuVal: this.resolvedSkuPartNumber }
				: { skusArr: this.allProductSkus };
			if (window.headerFunctions?.storeSelection) {
				this.window.headerFunctions.storeSelection(storeSelectionValues);
			}
		} else if (
			buttonType === ATC_BUTTON_TEXTS.noDeliveryLocation ||
			buttonType === ATC_BUTTON_TEXTS.enterZip
		) {
			this.window.headerFunctions.sameDayDelivery();
		} else if (
			buttonType === ATC_BUTTON_TEXTS.atc ||
			buttonType === ATC_BUTTON_TEXTS.preOrder
		) {
			this.store.dispatch(addToCartTriggered());
			this.addToCartButtonService.emitAddToCartClick();
		} else if (buttonType === ATC_BUTTON_TEXTS.customize) {
			this.addToCartButtonService.emitOpenCustomPanel(true);
		} else if (buttonType === ATC_BUTTON_TEXTS.notifyMe) {
			this.addToCartButtonService.emitOpenNotifyMePanel(true);
		}
	}

	/**
	 * @description Get the value from the Qty input element
	 * convert it to a number and set default value of 1
	 * or take the user entered value
	 * @param userInputQty - this is the key event made on the input element
	 * @param triggeredByState - was this function triggered by a state change (instead of the input element?)
	 */
	validateQuantity(userInputQty, triggeredByState = false) {
		const userInputQtyToNumber = this.utilityService.convertToNumber(userInputQty);
		this.checkMinMax();
		// set the quantity to either 1 or what the user entered
		if (
			userInputQtyToNumber === 0 ||
			userInputQtyToNumber === undefined ||
			isNaN(userInputQty) ||
			userInputQty === ''
		) {
			this.userInputQuantity = 1;
			this.setUserInputQuantityInState(1);
		} else if (userInputQtyToNumber > this.maxQty) {
			this.userInputQuantity = this.maxQty;
			this.setUserInputQuantityInState(this.maxQty);
		} else if (userInputQtyToNumber < this.minQty) {
			this.userInputQuantity = this.minQty;
			this.setUserInputQuantityInState(this.minQty);
		} else if (triggeredByState) {
			// if qty is valid and this was triggered by state, then the state is already updated but input isn't
			this.userInputQuantity = userInputQtyToNumber;
		} else {
			this.setUserInputQuantityInState(userInputQty);
		}
	}

	/**
	 * @description dispatch the action to update the user input quantity * in state
	 * @param quantity the value from the input HTML element
	 */
	setUserInputQuantityInState(quantity) {
		this.store.dispatch(
			userSetInputQuantity({
				atcUserInputQuantity: quantity.toString()
			})
		);
	}

	/**
	 * Checks the min an max value of a product
	 */
	checkMinMax() {
		const qty = this.utilityService.convertToNumber(this.userInputQuantity);

		const minMaxQtyObj: MinMaxQty = this.computeMinMaxQty(qty, this.minQty, this.maxQty);
		if (minMaxQtyObj.alertMessage) {
			this.store.dispatch(
				quantityRangeValidationFailed({
					message: minMaxQtyObj.alertMessage
				})
			);
		} else {
			this.store.dispatch(quantityRangeValidationSucceeded());
		}
	}

	/**
	 * @description
	 * Compute and reset the selections' quantity to minimum quantity if it is less or set it to maximum quantity if it exceeds it
	 */
	computeMinMaxQty(qty: number, minQty: number, maxQty: number): MinMaxQty {
		let tuple: MinMaxQty = {
			// Resetting the value of minMaxQty object to default
			alertMessage: '',
			value: qty
		};

		if (qty < minQty) {
			tuple = {
				value: minQty,
				alertMessage: `${DCSG_CONSTANTS.minQtyAlert.message} ${minQty}`
			};
		} else if (qty > maxQty) {
			tuple = {
				value: maxQty,
				alertMessage: `${DCSG_CONSTANTS.maxQtyAlert.message} ${maxQty}`
			};
		}
		return tuple;
	}

	@HostListener('window:HeaderServices:StoreSelection:Ready', ['$event'])
	public onStoreSelectionReady(customEvent: CustomEvent): void {
		if (customEvent) {
			this.storeSelectionReady = true;
		}
	}
}
